import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GlobalSearch from '../../components/GlobalSearch';
import { useAuth } from '../../contexts/AuthContext';
import { useDataMode } from '../../contexts/DataModeContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import NotificationBell from '../../components/NotificationBell';
import UserDropdownMenu from '../../components/UserDropdownMenu';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import SetupProgressBanner from '../../components/SetupProgressBanner';
import axios from 'axios';
import {
  LayoutDashboard, FileText, Inbox, Building2, User, Users,
  LogOut, Menu, X, Activity, Database, Zap, CreditCard, MessageSquare, Receipt, BarChart2,
  Rocket, TrendingUp, AlertTriangle, Search
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Sidebar Usage Mini-Widget ─────────────────────────────────────────────────
const SidebarUsageWidget = ({ token, billingPath, accentColor = 'blue' }) => {
  const { t } = useTranslation();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/billing/usage`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUsage(r.data))
      .catch(() => {});
  }, [token]);

  if (!usage || !usage.usage?.length) return null;

  const hasAtLimit   = usage.usage.some(u => !u.unlimited && u.current >= u.limit);
  const hasNearLimit = !hasAtLimit && usage.usage.some(u => !u.unlimited && u.current / u.limit >= 0.8);

  return (
    <div className="px-3 pb-3" data-testid="sidebar-usage-widget">
      <div className={`rounded-xl p-3 space-y-2.5 ${
        hasAtLimit   ? 'bg-red-50 border border-red-200' :
        hasNearLimit ? 'bg-amber-50 border border-amber-200' :
                       'bg-gray-50 border border-gray-100'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {hasAtLimit ? (
              <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
            ) : (
              <Activity className="h-3.5 w-3.5 text-gray-400" />
            )}
            <span className={`text-xs font-semibold ${hasAtLimit ? 'text-red-700' : 'text-gray-600'}`}>
              {usage.plan_name} {t('sidebar.plans')}
            </span>
          </div>
          <NavLink to={billingPath} className={`text-xs font-medium ${accentColor === 'blue' ? 'text-blue-600' : 'text-green-600'}`}>
            {t('sidebar.plans')} →
          </NavLink>
        </div>

        {/* Usage bars */}
        {usage.usage.map(item => {
          const pct = item.unlimited ? 0 : Math.min(100, Math.round((item.current / item.limit) * 100));
          const atLimit   = !item.unlimited && item.current >= item.limit;
          const nearLimit = !item.unlimited && pct >= 80 && !atLimit;
          const barColor  = atLimit ? 'bg-red-500' : nearLimit ? 'bg-amber-500' : accentColor === 'blue' ? 'bg-blue-500' : 'bg-green-500';
          const textColor = atLimit ? 'text-red-700 font-bold' : nearLimit ? 'text-amber-700 font-medium' : 'text-gray-600';
          return (
            <div key={item.key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">{item.label}</span>
                <span className={`text-xs ${textColor}`}>
                  {item.unlimited ? '∞' : `${item.current}/${item.limit}`}
                  {atLimit && ' 🔴'}
                </span>
              </div>
              {!item.unlimited && (
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                </div>
              )}
            </div>
          );
        })}

        {/* Upgrade CTA */}
        {hasAtLimit && (
          <NavLink
            to={billingPath}
            className="flex items-center justify-center gap-1.5 w-full text-xs font-bold text-white bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 px-3 py-2 rounded-lg transition-all"
            data-testid="sidebar-upgrade-btn"
          >
            <Rocket className="h-3 w-3" />
            {t('sidebar.planUpgrade')}
          </NavLink>
        )}
        {hasNearLimit && !hasAtLimit && (
          <NavLink
            to={billingPath}
            className={`flex items-center justify-center gap-1.5 w-full text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-all`}
          >
            <TrendingUp className="h-3 w-3" />
            {t('sidebar.approachingLimit')}
          </NavLink>
        )}
      </div>
    </div>
  );
};

const ExhibitorLayout = () => {
  const { user, token, logout } = useAuth();
  const { isMockMode, toggleMode } = useDataMode();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [pendingQuotes, setPendingQuotes] = useState(0);

  const handleLogout = async () => { await logout(); navigate('/'); };

  useEffect(() => {
    if (token && !isMockMode) {
      axios.get(`${API}/exhibitor/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setOrgName(res.data.org?.display_name || ''))
        .catch(() => {});
    } else {
      setOrgName('');
    }
  }, [token, isMockMode]);

  // Unread messages badge
  useEffect(() => {
    if (!token) return;
    const fetchUnread = () => {
      axios.get(`${API}/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUnreadMsg(r.data.unread || 0))
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [token]);

  // Pending quotes badge (poll every 60s)
  useEffect(() => {
    if (!token) return;
    if (isMockMode) { setPendingQuotes(0); return; }
    const fetchPending = () => {
      axios.get(`${API}/exhibitor/proposals?status=pending&limit=50`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setPendingQuotes((r.data?.items || []).length))
        .catch(() => {});
    };
    fetchPending();
    const t = setInterval(fetchPending, 60000);
    return () => clearInterval(t);
  }, [token, isMockMode]);

  const navItems = [
    { label: t('sidebar.dashboard'),       icon: LayoutDashboard, path: '/app/exhibitor/dashboard'         },
    { label: t('sidebar.briefs'),          icon: FileText,         path: '/app/exhibitor/briefs'           },
    { label: t('sidebar.analytics'),       icon: BarChart2,        path: '/app/exhibitor/analytics'        },
    { label: t('sidebar.receivedQuotes'),  icon: Inbox,            path: '/app/exhibitor/proposals', badge: 'quotes' },
    { label: t('sidebar.findContractors'), icon: Search,           path: '/app/exhibitor/find-contractors' },
    { label: t('sidebar.messages'),        icon: MessageSquare,    path: '/app/exhibitor/messages'         },
    { label: t('sidebar.invoices'),        icon: Receipt,          path: '/app/exhibitor/invoices'         },
    { label: t('sidebar.publicProfile'),   icon: User,             path: '/app/exhibitor/my-profile'       },
    { label: t('sidebar.companyProfile'),  icon: Building2,        path: '/app/exhibitor/profile'          },
    { label: t('sidebar.team'),            icon: Users,            path: '/app/exhibitor/team'             },
    { label: t('sidebar.billing'),         icon: CreditCard,       path: '/app/exhibitor/billing'          },
  ];

  return (
    <>
      <GlobalSearch />
      <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 flex flex-col fixed inset-y-0 left-0 lg:static w-64 bg-white shadow-lg z-30 transition-transform duration-300 ease-in-out`}>
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">Exhibitor Portal</p>
                <p className="text-xs text-gray-400">{isMockMode ? 'Expo Stand' : (orgName || user?.name || 'My Company')}</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map(item => {
            const Icon = item.icon;
            const isMessages = item.path.includes('/messages');
            const isQuotes = item.badge === 'quotes';
            return (
              <NavLink key={item.path} to={item.path}
                onClick={() => { setSidebarOpen(false); if (isMessages) setUnreadMsg(0); if (isQuotes) setPendingQuotes(0); }}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-gray-600 hover:bg-gray-100'
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
                {isMessages && unreadMsg > 0 && (
                  <span className="bg-blue-600 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold px-1">
                    {unreadMsg}
                  </span>
                )}
                {isQuotes && pendingQuotes > 0 && (
                  <span className="relative flex items-center justify-center" data-testid="pending-quotes-badge">
                    <span className="animate-ping absolute inline-flex h-5 w-5 rounded-full bg-blue-400 opacity-60" />
                    <span className="relative inline-flex h-5 min-w-[20px] rounded-full bg-blue-600 text-white text-[10px] font-bold items-center justify-center px-1">
                      {pendingQuotes > 9 ? '9+' : pendingQuotes}
                    </span>
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-100">
          {/* Setup Progress Banner */}
          <SetupProgressBanner
            role="exhibitor"
            accentColor="blue"
            token={token}
            onboardingUrl="/app/exhibitor/onboarding"
            doneKey="exhibitor_onboarding_done"
            profileApi="/exhibitor/profile-completion"
            totalSteps={5}
          />
          {/* Usage Widget */}
          <SidebarUsageWidget token={token} billingPath="/app/exhibitor/billing" accentColor="blue" />
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" size="sm" className="w-full text-gray-600 border-gray-200 hover:bg-gray-50">
            <LogOut className="w-3.5 h-3.5 mr-2" />{t('sidebar.logout')}
          </Button>
        </div>
      </div>

      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded hover:bg-gray-100">
                <Menu className="w-5 h-5" />
              </button>
              <Badge className="bg-blue-100 text-blue-700 border-blue-200">Exhibitor</Badge>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button onClick={toggleMode} className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-all ${isMockMode ? 'bg-white shadow text-amber-700' : 'text-gray-500 hover:text-gray-700'}`}>
                  <Database className="w-3.5 h-3.5" /><span className="hidden sm:inline">Mock</span>
                </button>
                <button onClick={toggleMode} className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-all ${!isMockMode ? 'bg-white shadow text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`}>
                  <Zap className="w-3.5 h-3.5" /><span className="hidden sm:inline">Live</span>
                </button>
              </div>
              <LanguageSwitcher variant="dark" />
              <NotificationBell accentColor="blue" />
              <UserDropdownMenu dashboardRoute="/app/exhibitor/dashboard" />
            </div>
          </div>
        </header>

        <main className="flex-1 p-3 sm:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
    </>
  );
};

export default ExhibitorLayout;
